import { useState, type ReactNode } from 'react';
import { Copy, Check, ShieldCheck, Mail, Globe2, Cloud, ListChecks, AtSign } from 'lucide-react';
import { useOrg } from '../auth/OrgContext';
import { Card, Badge, cx } from '../components/ui';

// Campaign deliverability configuration (per-Org). Auto-configure (M365/Google super-admin app
// install — auto-verifies ownership + safelists) is the recommended path; manual safelisting +
// domain-ownership verification work anywhere. The live connectors (Graph/Admin install, DNS/email
// challenge) are the next backend unit; the instructions + walkthroughs here are real and usable.

// Pavlov's shared sending domains (the pre-warmed pool) + the simulation header to allowlist.
const SENDING_DOMAINS = [
  'mail.secure-msg.net',
  'notify.account-alerts.net',
  'team.workspace-docs.net',
];
const SIM_HEADER = 'X-Pavlov-Simulation: true';
const SPF_INCLUDE = 'include:_spf.runpavlov.ai';

function CopyRow({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2 rounded-input border border-line bg-ink/[0.02] px-3 py-2">
      <code className="flex-1 break-all font-mono text-xs text-ink">{value}</code>
      <button
        onClick={() => void navigator.clipboard?.writeText(value).then(() => setCopied(true))}
        className="shrink-0 text-muted transition-colors hover:text-ink"
        title="Copy"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="mt-3 space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-sm text-muted">
          <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full border border-line text-[11px] font-medium text-ink">
            {i + 1}
          </span>
          <span className="leading-relaxed">{it}</span>
        </li>
      ))}
    </ol>
  );
}

function AutoCard({
  provider,
  icon: Icon,
  blurb,
  steps,
}: {
  provider: string;
  icon: typeof Cloud;
  blurb: string;
  steps: ReactNode[];
}) {
  const [requested, setRequested] = useState(false);
  return (
    <Card className="flex flex-col p-5">
      <div className="flex items-start justify-between">
        <span className="grid h-9 w-9 place-items-center rounded-lg border border-line text-ink">
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <Badge tone="muted">Auto-verifies ownership</Badge>
      </div>
      <div className="mt-3 font-display text-lg text-ink">{provider}</div>
      <div className="mt-1 text-sm text-muted">{blurb}</div>
      <Steps items={steps} />
      <div className="flex-1" />
      <button
        onClick={() => setRequested(true)}
        disabled={requested}
        className="btn-primary mt-4 w-full"
      >
        {requested ? (
          <>
            <Check className="h-4 w-4" /> Setup requested
          </>
        ) : (
          <>Connect {provider}</>
        )}
      </button>
      {requested && (
        <p className="mt-2 text-xs text-muted">
          We’ll send a super-admin install link to complete the connection. Until then, use Manual
          setup below.
        </p>
      )}
    </Card>
  );
}

function SafelistCard({ provider, steps }: { provider: string; steps: ReactNode[] }) {
  return (
    <Card className="p-5">
      <div className="font-display text-base text-ink">{provider}</div>
      <Steps items={steps} />
    </Card>
  );
}

export function ConfigurationSection() {
  const { selectedOrg } = useOrg();
  const target = selectedOrg ? selectedOrg.name : 'your target Orgs';
  const domain = selectedOrg
    ? `${selectedOrg.name.toLowerCase().replace(/[^a-z0-9]+/g, '')}.com`
    : 'company.com';

  const [verifyEmail, setVerifyEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [dnsChecked, setDnsChecked] = useState(false);

  return (
    <div className="space-y-10">
      <p className="max-w-content text-sm text-muted">
        Configure how Pavlov’s simulated emails reach <span className="text-ink">{target}</span>{' '}
        without being blocked or filtered. Auto-configure is fastest; manual setup works on any mail
        platform.
      </p>

      {/* Auto-configure */}
      <section>
        <div className="eyebrow mb-3">Auto-configure · recommended</div>
        <div className="grid gap-4 lg:grid-cols-2">
          <AutoCard
            provider="Microsoft 365"
            icon={Cloud}
            blurb="Install the Pavlov app in the tenant as a Global Admin — safelists delivery and confirms domain ownership in one step."
            steps={[
              'Click Connect and sign in as a Microsoft 365 Global Administrator.',
              'Review and grant the Pavlov app the requested admin consent.',
              'Pavlov installs a delivery connector + Tenant Allow entries and verifies ownership automatically.',
            ]}
          />
          <AutoCard
            provider="Google Workspace"
            icon={Globe2}
            blurb="Install the Pavlov app from the Marketplace as a Super Admin — sets the inbound gateway/allowlist and confirms ownership."
            steps={[
              'Click Connect and sign in as a Google Workspace Super Admin.',
              'Approve the Pavlov Marketplace app for your organization.',
              'Pavlov configures the email allowlist + spam bypass and verifies ownership automatically.',
            ]}
          />
        </div>
      </section>

      {/* Manual safelisting */}
      <section>
        <div className="eyebrow mb-3 flex items-center gap-2">
          <ListChecks className="h-4 w-4" /> Manual · safelist Pavlov
        </div>
        <Card className="mb-4 p-5">
          <div className="text-sm text-muted">
            Allowlist Pavlov’s sending domains and the simulation header so tests bypass spam
            filtering. Add these wherever you manage mail flow:
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {SENDING_DOMAINS.map((d) => (
              <CopyRow key={d} value={d} />
            ))}
            <CopyRow value={SIM_HEADER} />
            <CopyRow value={SPF_INCLUDE} />
          </div>
        </Card>
        <div className="grid gap-4 lg:grid-cols-3">
          <SafelistCard
            provider="Microsoft 365"
            steps={[
              <>
                Defender → Policies → <span className="text-ink">Tenant Allow/Block Lists</span> →
                add the sending domains as Allow.
              </>,
              <>
                Add a mail-flow rule: if header{' '}
                <code className="font-mono text-ink">X-Pavlov-Simulation</code> is present, set SCL
                to -1 (bypass spam).
              </>,
              <>
                Enable <span className="text-ink">Enhanced Filtering / Advanced Delivery</span> for
                the Pavlov phishing-simulation entry.
              </>,
            ]}
          />
          <SafelistCard
            provider="Google Workspace"
            steps={[
              <>
                Admin → Apps → Gmail → <span className="text-ink">Spam, Phishing and Malware</span>{' '}
                → add an Email Allowlist for the sending domains.
              </>,
              <>
                Add a Content Compliance rule matching{' '}
                <code className="font-mono text-ink">X-Pavlov-Simulation</code> → bypass spam.
              </>,
              <>Optionally set the domains as an Inbound Gateway for full delivery.</>,
            ]}
          />
          <SafelistCard
            provider="Other / Secure Gateway"
            steps={[
              <>
                Allowlist the sending domains in your secure email gateway (Proofpoint, Mimecast,
                etc.).
              </>,
              <>
                Whitelist by the <code className="font-mono text-ink">X-Pavlov-Simulation</code>{' '}
                header or envelope domain.
              </>,
              <>Ensure URL-rewriting / link-protection excludes the Pavlov link domains.</>,
            ]}
          />
        </div>
      </section>

      {/* Verify domain ownership */}
      <section>
        <div className="eyebrow mb-3 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Manual · verify domain ownership
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="flex flex-col p-5">
            <div className="flex items-center gap-2 font-display text-base text-ink">
              <Globe2 className="h-4 w-4" /> DNS record
            </div>
            <p className="mt-1 text-sm text-muted">
              Add this TXT record to <span className="font-mono text-ink">{domain}</span>, then
              verify:
            </p>
            <div className="mt-3 space-y-2">
              <div className="text-xs uppercase tracking-wide text-muted">Host</div>
              <CopyRow value={`_pavlov-challenge.${domain}`} />
              <div className="text-xs uppercase tracking-wide text-muted">Value</div>
              <CopyRow value={`pavlov-verify=${selectedOrg ? selectedOrg.id : 'org_xxxxxxxx'}`} />
            </div>
            <div className="flex-1" />
            <button
              onClick={() => setDnsChecked(true)}
              className={cx('btn-outline mt-4 w-full', dnsChecked && 'opacity-70')}
            >
              {dnsChecked ? 'Checking DNS…' : 'Verify via DNS'}
            </button>
            {dnsChecked && (
              <p className="mt-2 text-xs text-muted">
                DNS propagation can take up to an hour; we’ll mark the Org verified once the record
                is visible.
              </p>
            )}
          </Card>

          <Card className="flex flex-col p-5">
            <div className="flex items-center gap-2 font-display text-base text-ink">
              <Mail className="h-4 w-4" /> Email confirmation
            </div>
            <p className="mt-1 text-sm text-muted">
              Send a one-click confirmation to a domain administrator:
            </p>
            <div className="mt-3 flex items-center gap-2">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-input border border-line text-muted">
                <AtSign className="h-4 w-4" />
              </span>
              <input
                value={verifyEmail}
                onChange={(e) => setVerifyEmail(e.target.value)}
                placeholder={`admin@${domain}`}
                className="input"
              />
            </div>
            <div className="flex-1" />
            <button
              onClick={() => setEmailSent(true)}
              disabled={!verifyEmail || emailSent}
              className="btn-outline mt-4 w-full"
            >
              {emailSent ? (
                <>
                  <Check className="h-4 w-4" /> Confirmation sent
                </>
              ) : (
                'Send verification email'
              )}
            </button>
            {emailSent && (
              <p className="mt-2 text-xs text-muted">
                Sent to {verifyEmail}. The Org is verified as soon as they click the link.
              </p>
            )}
          </Card>
        </div>
      </section>
    </div>
  );
}
